// src/hooks/useSignalR.ts
//
// Thin React hook over @microsoft/signalr for the Helion dashboards. Opens a
// connection to the `helion` serverless SignalR hub via the backend /negotiate
// endpoint, subscribes to ONE server-push target, and reports connection liveness
// so callers can keep their existing HTTP poll as an automatic fallback.
//
// Guardrails (see HelionBackend/docs/SIGNALR_INTEGRATION_BRIEF.md — the SignalR docs
// live in the backend repo alongside the broadcasters): this channel is RECEIVE-ONLY
// display telemetry. The hook never invokes a server method — it only listens —
// so it cannot mutate trading state. If SignalR is unavailable the caller's poll
// keeps the surface live; failures here are logged, never thrown.
//
// FUND LABEL (M3 Step 4, CIO decision 8): every push now carries `fund`. A push whose
// `fund` names a fund other than the one this page displays (Horizon until the fund
// switch, M7) is DROPPED here, before any caller sees it, so another fund's figures can
// never be drawn under Horizon's. A push with no `fund` is the fund of record's
// (backwards compatible). See src/lib/pushFund.ts.

import { useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { API_BASE_URL } from "@lib/config";
import { acceptPushForFund, DISPLAYED_FUND } from "@lib/pushFund";

type UseSignalROptions = {
  /** When false, the hook does not connect (e.g. while auth is still loading). */
  enabled?: boolean;
  /** The fund this page displays; pushes labelled for another fund are ignored. Horizon until M7. */
  fund?: string;
};

/**
 * Subscribe to a single SignalR push `target` on the `helion` hub.
 *
 * @returns `{ connected }` — true once the websocket is established. Callers
 *          should keep polling whenever `connected` is false so the surface
 *          degrades gracefully if SignalR is down.
 */
export const useSignalR = (
  target: string,
  onMessage: (payload: any) => void,
  options: UseSignalROptions = {}
): { connected: boolean } => {
  const { enabled = true, fund = DISPLAYED_FUND } = options;
  const [connected, setConnected] = useState(false);

  // Keep the latest callback without forcing a reconnect when it changes.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  // Likewise the displayed fund: read at message time, no reconnect.
  const fundRef = useRef(fund);
  fundRef.current = fund;

  useEffect(() => {
    if (!enabled) return;
    // No API base configured → caller stays entirely on its HTTP poll fallback.
    if (!API_BASE_URL) return;

    let isMounted = true;

    // @microsoft/signalr appends "/negotiate" to this base. API_BASE_URL already
    // includes "/api", so the negotiate call lands on /api/negotiate. withCredentials
    // forwards the SWA EasyAuth cookie so the negotiate function sees the principal.
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}`, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(target, (payload: any) => {
      if (!isMounted) return;
      if (!acceptPushForFund(payload, fundRef.current)) {
        // Another fund's push (or an unreadable label): never shown on this page.
        return;
      }
      onMessageRef.current(payload);
    });

    connection.onreconnecting(() => {
      if (isMounted) setConnected(false);
    });
    connection.onreconnected(() => {
      if (isMounted) setConnected(true);
    });
    connection.onclose(() => {
      if (isMounted) setConnected(false);
    });

    connection
      .start()
      .then(() => {
        if (isMounted) setConnected(true);
      })
      .catch((err) => {
        // Connection failed (service down, negotiate 401, CORS, offline). The
        // caller's poll keeps the surface live — log, don't throw.
        console.warn("[useSignalR] connect failed; falling back to poll", err);
        if (isMounted) setConnected(false);
      });

    return () => {
      isMounted = false;
      setConnected(false);
      // stop() is async and may race a still-starting connection; swallow it.
      connection.stop().catch(() => {
        /* ignore stop races on unmount */
      });
    };
  }, [enabled, target]);

  return { connected };
};
